import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import { MultiSelect, type OptionValue } from "@/components/ui/multi-select";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";

type Props = {
  name: string;
  disabled?: boolean;
  label?: string;
  options: {
    value: OptionValue;
    label: string;
    icon?: React.ComponentType<{ className?: string }>;
    description?: string;
  }[];
  placeholder?: string;
  description?: string;
  className?: React.HTMLAttributes<HTMLButtonElement>["className"];
  formItemClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
  variant?: "default" | "secondary" | "destructive" | "inverted";
  maxCount?: number;
  modalPopover?: boolean;
  disabledValues?: string[];
  mode?: "single" | "multiple";
  highlightWhenSelected?: boolean;
  enableClear?: boolean;
  icon?: React.ElementType;
};

export const MultiSelectInput = ({
  label,
  name,
  icon,
  options,
  disabled = false,
  className,
  description,
  placeholder,
  maxCount = 10,
  modalPopover = false,
  disabledValues = [],
  formItemClassName,
  mode = "multiple",
  highlightWhenSelected = true,
  enableClear = true,
}: Props) => {
  const form = useFormContext();
  const { error } = useFormField();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const handleValueChange = (values: OptionValue[] | null) => {
          field.onChange(values);
        };

        return (
          <FormItem className={formItemClassName}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <MultiSelect
                options={options}
                mode={mode}
                onValueChange={handleValueChange}
                defaultValue={field.value || []}
                placeholder={placeholder}
                maxCount={maxCount}
                modalPopover={modalPopover}
                disabledValues={disabledValues}
                className={cn(
                  Array.isArray(field.value) &&
                    field.value.length > 0 &&
                    highlightWhenSelected
                    ? "bg-secondary"
                    : undefined,
                  className,
                )}
                disabled={disabled}
                aria-invalid={error ? true : undefined}
                enableClear={enableClear}
                icon={icon}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
